/**
 * 租户管理页面 — 适配 enterprise-brain 亮色主题
 * 移植自 frontend/src/modules/tenant/TenantManageView.tsx
 */
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/components/Toast/useToast';
import * as tenantApi from '@/services/tenant';
import type { Tenant, TenantUser } from '@/services/tenant';
import { request } from '@/services/api';
import './TenantsView.css';

const roleLabels: Record<string, string> = {
  superadmin: '超级管理员',
  admin: '管理员',
  member: '成员',
  viewer: '访客',
};

function getRoleLabel(role: string) {
  return roleLabels[role] || role;
}

function getStatusColor(s: string) {
  return s === 'active' ? 'var(--green)' : s === 'suspended' ? 'var(--red)' : 'var(--muted)';
}

function getStatusText(s: string) {
  return s === 'active' ? '运行中' : s === 'suspended' ? '已暂停' : '已禁用';
}

// 随机密码生成
function genPassword(): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const all = upper + lower + digits;
  let pwd = upper[Math.floor(Math.random() * upper.length)]
    + lower[Math.floor(Math.random() * lower.length)]
    + digits[Math.floor(Math.random() * digits.length)];
  for (let i = 0; i < 5; i++) pwd += all[Math.floor(Math.random() * all.length)];
  return pwd.split('').sort(() => Math.random() - 0.5).join('');
}

interface FullUser {
  id: string;
  tenant_id?: string;
  role: string;
  name?: string;
  email?: string;
  initial_password?: string;
  password_changed?: boolean;
  avatar_color?: string;
  avatar_text?: string;
  status?: string;
}

export function TenantsView() {
  const toast = useToast();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Tenant | null>(null);
  const [users, setUsers] = useState<FullUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      setTenants(await tenantApi.listTenants());
    } catch {
      toast.error('加载租户列表失败');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const loadUsers = async (tenantId: string) => {
    setUsersLoading(true);
    try {
      const data = await tenantApi.getTenantUsers(tenantId) as FullUser[];
      setUsers(data);
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSelect = (t: Tenant) => {
    setSelected(t);
    loadUsers(t.id);
  };

  const handleCreate = async (data: tenantApi.CreateTenantRequest) => {
    try {
      await tenantApi.createTenant(data);
      toast.success('租户创建成功');
      setShowCreate(false);
      loadTenants();
    } catch (err) {
      toast.error(`创建失败: ${err instanceof Error ? err.message : '请求失败'}`);
    }
  };

  const handleEdit = async (data: Partial<Tenant> & { reset_password?: string }) => {
    if (!selected) return;
    try {
      await tenantApi.updateTenant(selected.id, data);
      toast.success('租户更新成功');
      setShowEdit(false);
      const updated = await tenantApi.listTenants();
      setTenants(updated);
      setSelected(updated.find(t => t.id === selected.id) || null);
    } catch (err) {
      toast.error(`更新失败: ${err instanceof Error ? err.message : '请求失败'}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (id === 'default') { toast.error('不能删除默认租户'); return; }
    if (!confirm('确定要删除该租户吗？此操作不可恢复！')) return;
    try {
      await tenantApi.deleteTenant(id);
      toast.success('租户已删除');
      loadTenants();
      if (selected?.id === id) { setSelected(null); setUsers([]); }
    } catch { toast.error('操作失败'); }
  };

  const handleAddUser = async (data: { name: string; email: string; password: string; role: string }) => {
    if (!selected) return;
    try {
      await request(`/tenants/${selected.id}/users/create`, {
        method: 'POST',
        body: data,
      });
      toast.success('用户创建成功');
      setShowAddUser(false);
      loadUsers(selected.id);
    } catch (err) {
      toast.error(`创建失败: ${err instanceof Error ? err.message : '请求失败'}`);
    }
  };

  const handleRoleChange = async (userId: string, role: string) => {
    if (!selected) return;
    try {
      await request(`/tenants/${selected.id}/users/${userId}/role`, {
        method: 'PUT',
        body: { role },
      });
      toast.success('角色已更新');
      loadUsers(selected.id);
    } catch { toast.error('更新失败'); }
  };

  const handleRemoveUser = async (userId: string, userName: string) => {
    if (!selected) return;
    if (!confirm(`确定要从租户移除用户「${userName}」吗？`)) return;
    try {
      await tenantApi.removeUserFromTenant(selected.id, userId);
      toast.success('用户已移除');
      loadUsers(selected.id);
    } catch { toast.error('移除失败'); }
  };

  return (
    <div className="doc-page wide without-heading tenants-view">
      <div className="tenants-header">
        <div>
          <h1 className="tenants-title">租户管理</h1>
          <p className="tenants-subtitle">管理多租户企业接入</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M10 3v14M3 10h14"/></svg>
          创建租户
        </button>
      </div>

      <div className="tenants-layout">
        {/* 左侧列表 */}
        <div className="tenants-list">
          <div className="tenants-list-head">
            <span>租户列表</span>
            <span className="tenants-count">{tenants.length} 个</span>
          </div>
          {loading ? (
            <div className="tenants-loading"><div className="loading-spinner" />加载中…</div>
          ) : (
            <div>
              {tenants.map(t => (
                <div
                  key={t.id}
                  className={`tenants-item${selected?.id === t.id ? ' active' : ''}`}
                  onClick={() => handleSelect(t)}
                >
                  <span className="tenants-item-avatar">
                    {(t.display_name || t.name).charAt(0).toUpperCase()}
                  </span>
                  <div className="tenants-item-info">
                    <div className="tenants-item-name">{t.display_name || t.name}</div>
                    <div className="tenants-item-id">/{t.id}</div>
                  </div>
                  <div className="tenants-item-status" style={{ color: getStatusColor(t.status) }}>
                    <span className="tenants-dot" style={{ background: getStatusColor(t.status) }} />
                    {getStatusText(t.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 右侧详情 */}
        <div className="tenants-detail">
          {selected ? (
            <>
              <div className="tenants-detail-head">
                <span className="tenants-detail-avatar">
                  {(selected.display_name || selected.name).charAt(0).toUpperCase()}
                </span>
                <div className="tenants-detail-title">
                  <h2>{selected.display_name || selected.name}</h2>
                  <span className="tenants-detail-path">访问路径: /{selected.id}</span>
                </div>
                <div className="tenants-detail-actions">
                  <button className="btn btn-secondary" onClick={() => setShowEdit(true)}>编辑</button>
                  {selected.id !== 'default' && (
                    <button className="btn btn-secondary" style={{ color: 'var(--red)' }}
                      onClick={() => handleDelete(selected.id)}>删除</button>
                  )}
                </div>
              </div>

              {/* 基本信息 */}
              <div className="tenants-info-grid">
                {[
                  ['租户 ID', selected.id],
                  ['租户名称', selected.name],
                  ['显示名称', selected.display_name || '-'],
                  ['套餐类型', selected.plan_type || 'pro'],
                  ['席位使用', `${selected.used_seats} / ${selected.total_seats}`],
                  ['状态', getStatusText(selected.status)],
                  ['创建时间', new Date(selected.created_at).toLocaleString()],
                  ...(selected.custom_domain ? [['自定义域名', selected.custom_domain]] : []),
                ].map(([label, value]) => (
                  <div key={label} className="tenants-info-item">
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </div>

              {/* 用户列表 */}
              <div className="tenants-users-section">
                <div className="tenants-users-head">
                  <span>用户列表</span>
                  <span className="tenants-count">{users.length} 人</span>
                  <button className="btn btn-secondary tenants-add-user-btn" onClick={() => setShowAddUser(true)}>
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M10 3v14M3 10h14"/></svg>
                    添加用户
                  </button>
                </div>

                {usersLoading ? (
                  <div className="tenants-loading"><div className="loading-spinner" style={{ width: 18, height: 18 }} />加载中…</div>
                ) : users.length === 0 ? (
                  <div className="tenants-empty">暂无用户</div>
                ) : (
                  <table className="tenants-users-table">
                    <thead>
                      <tr>
                        <th>用户</th>
                        <th>邮箱</th>
                        <th>初始密码</th>
                        <th>角色</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.id}>
                          <td>
                            <div className="tenants-user-cell">
                              <span className="tenants-user-avatar" style={{ background: u.avatar_color || 'var(--selected)' }}>
                                {u.avatar_text || u.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                              <span className="tenants-user-name">{u.name || '-'}</span>
                            </div>
                          </td>
                          <td className="tenants-user-email">{u.email || '-'}</td>
                          <td>
                            {u.password_changed ? (
                              <span className="tenants-pwd-changed">••••••••</span>
                            ) : u.initial_password ? (
                              <code className="tenants-pwd-initial">{u.initial_password}</code>
                            ) : (
                              <span className="tenants-pwd-changed">—</span>
                            )}
                          </td>
                          <td>
                            <select
                              className="tenants-role-select"
                              value={u.role}
                              onChange={e => handleRoleChange(u.id, e.target.value)}
                            >
                              <option value="admin">管理员</option>
                              <option value="member">普通用户</option>
                            </select>
                          </td>
                          <td>
                            <button
                              className="btn btn-icon tenants-remove-btn"
                              title="移除用户"
                              onClick={() => handleRemoveUser(u.id, u.name || u.email || '')}
                            >
                              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                                <path d="M3 5h14M7 5V2h6v3M5 5l1 13h8l1-13"/>
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <div className="tenants-empty-state">
              <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="48" height="48" style={{ opacity: 0.3 }}>
                <path d="M24 4 4 14v20l20 10 20-10V14L24 4Zm0 10v20M4 14l20 10 20-10"/>
              </svg>
              <p>选择一个租户查看详情</p>
            </div>
          )}
        </div>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onSubmit={handleCreate} />}
      {showEdit && selected && <EditModal tenant={selected} onClose={() => setShowEdit(false)} onSubmit={handleEdit} />}
      {showAddUser && selected && (
        <AddUserModal
          tenantId={selected.id}
          onClose={() => setShowAddUser(false)}
          onSubmit={handleAddUser}
        />
      )}
    </div>
  );
}

function CreateModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (d: tenantApi.CreateTenantRequest) => void }) {
  const [form, setForm] = useState({ id: '', name: '', display_name: '', plan_type: 'pro', total_seats: 50, admin_email: '', admin_password: '', admin_name: '' });
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: string | number) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(form); } finally { setBusy(false); }
  };

  return (
    <div className="modal-mask open" onClick={onClose}>
      <div className="dialog tenants-modal" onClick={e => e.stopPropagation()}>
        <header className="dialog-head">
          <h2>创建租户</h2>
          <button className="btn btn-icon" onClick={onClose} aria-label="关闭">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" width="18" height="18"><path d="m5 5 10 10M5 15 15 5"/></svg>
          </button>
        </header>
        <form onSubmit={submit}>
          <div className="dialog-body">
            <div className="tm-section-title">租户信息</div>
            <div className="tm-field">
              <label>租户 ID <em>必填</em></label>
              <input value={form.id} onChange={e => set('id', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} placeholder="如: nuphy（用于 URL 路径）" required pattern="[a-z0-9_\-]{3,32}" />
              <small>3-32位，仅支持小写字母、数字、下划线、连字符</small>
            </div>
            <div className="tm-field"><label>租户名称 <em>必填</em></label><input value={form.name} onChange={e => set('name', e.target.value)} required /></div>
            <div className="tm-field"><label>显示名称</label><input value={form.display_name} onChange={e => set('display_name', e.target.value)} placeholder="默认同租户名称" /></div>
            <div className="tm-row">
              <div className="tm-field">
                <label>套餐类型</label>
                <select value={form.plan_type} onChange={e => set('plan_type', e.target.value)}>
                  <option value="free">免费版</option>
                  <option value="pro">专业版</option>
                  <option value="enterprise">企业版</option>
                </select>
              </div>
              <div className="tm-field"><label>席位数</label><input type="number" min={1} max={9999} value={form.total_seats} onChange={e => set('total_seats', parseInt(e.target.value) || 50)} /></div>
            </div>
            <div className="tm-section-title" style={{ marginTop: 20 }}>租户管理员</div>
            <div className="tm-field"><label>管理员邮箱 <em>必填</em></label><input type="email" value={form.admin_email} onChange={e => set('admin_email', e.target.value)} required /></div>
            <div className="tm-field"><label>登录密码 <em>必填</em></label><input type="password" value={form.admin_password} onChange={e => set('admin_password', e.target.value)} minLength={6} required /><small>至少6位，首次登录后可修改</small></div>
            <div className="tm-field"><label>管理员名称</label><input value={form.admin_name} onChange={e => set('admin_name', e.target.value)} placeholder="默认使用租户名称 + Admin" /></div>
          </div>
          <footer className="dialog-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>取消</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? '创建中…' : '创建租户'}</button>
          </footer>
        </form>
      </div>
    </div>
  );
}

/* ===== 编辑弹窗（两步：填写→确认）===== */
function EditModal({ tenant, onClose, onSubmit }: { tenant: Tenant; onClose: () => void; onSubmit: (d: Partial<Tenant> & { reset_password?: string }) => void }) {
  const [form, setForm] = useState({ name: tenant.name, display_name: tenant.display_name || '', plan_type: tenant.plan_type || 'pro', total_seats: tenant.total_seats || 50, status: tenant.status, reset_password: '' });
  const [step, setStep] = useState<'edit' | 'confirm'>('edit');
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: string | number) => setForm(f => ({ ...f, [k]: v }));

  const planLabels: Record<string, string> = { free: '免费版', pro: '专业版', enterprise: '企业版' };
  const statusLabels: Record<string, string> = { active: '运行中', inactive: '已禁用', suspended: '已暂停' };

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const data: Partial<Tenant> & { reset_password?: string } = { ...form };
      if (!form.reset_password) delete data.reset_password;
      await onSubmit(data);
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-mask open" onClick={onClose}>
      <div className="dialog tenants-modal" onClick={e => e.stopPropagation()}>
        <header className="dialog-head">
          <h2>{step === 'edit' ? '编辑租户' : '确认修改'}</h2>
          <button className="btn btn-icon" onClick={onClose} aria-label="关闭">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" width="18" height="18"><path d="m5 5 10 10M5 15 15 5"/></svg>
          </button>
        </header>

        {step === 'edit' ? (
          <>
            <div className="dialog-body">
              <div className="tm-section-title">基本信息</div>
              <div className="tm-field"><label>租户 ID</label><input value={tenant.id} disabled /><small>创建后不可修改</small></div>
              <div className="tm-field"><label>租户名称 <em>必填</em></label><input value={form.name} onChange={e => set('name', e.target.value)} required /></div>
              <div className="tm-field"><label>显示名称</label><input value={form.display_name} onChange={e => set('display_name', e.target.value)} /></div>
              <div className="tm-row">
                <div className="tm-field">
                  <label>套餐类型</label>
                  <select value={form.plan_type} onChange={e => set('plan_type', e.target.value)}>
                    <option value="free">免费版</option><option value="pro">专业版</option><option value="enterprise">企业版</option>
                  </select>
                </div>
                <div className="tm-field"><label>席位数</label><input type="number" min={1} max={9999} value={form.total_seats} onChange={e => set('total_seats', parseInt(e.target.value) || 50)} /></div>
              </div>
              <div className="tm-field">
                <label>状态</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="active">运行中</option><option value="inactive">已禁用</option><option value="suspended">已暂停</option>
                </select>
              </div>
              <div className="tm-section-title" style={{ marginTop: 20 }}>重置管理员密码</div>
              <div className="tm-field"><label>新密码</label><input type="password" value={form.reset_password} onChange={e => set('reset_password', e.target.value)} placeholder="留空则不修改" minLength={6} /><small>至少6位，留空则保持原密码</small></div>
            </div>
            <footer className="dialog-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>取消</button>
              <button type="button" className="btn btn-primary" onClick={() => setStep('confirm')}>下一步</button>
            </footer>
          </>
        ) : (
          <>
            <div className="dialog-body">
              <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>请确认以下变更内容，保存后立即生效。</p>
              <div className="tenants-confirm-list">
                {[
                  ['租户 ID', tenant.id, tenant.id],
                  ['租户名称', tenant.name, form.name],
                  ['显示名称', tenant.display_name || '-', form.display_name || '-'],
                  ['套餐类型', planLabels[tenant.plan_type] || tenant.plan_type, planLabels[form.plan_type] || form.plan_type],
                  ['席位数', String(tenant.total_seats), String(form.total_seats)],
                  ['状态', statusLabels[tenant.status], statusLabels[form.status] || form.status],
                  ...(form.reset_password ? [['管理员密码', '（原密码）', '（已更改）']] : []),
                ].map(([label, before, after]) => (
                  <div key={label} className={`tenants-confirm-row${before !== after ? ' changed' : ''}`}>
                    <span className="tenants-confirm-label">{label}</span>
                    <span className="tenants-confirm-before">{before}</span>
                    {before !== after && (
                      <>
                        <span className="tenants-confirm-arrow">→</span>
                        <span className="tenants-confirm-after">{after}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <footer className="dialog-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setStep('edit')}>返回修改</button>
              <button type="button" className="btn btn-primary" disabled={busy} onClick={handleConfirm}>
                {busy ? '保存中…' : '确认保存'}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}


/* ===== 添加用户弹窗 ===== */
function AddUserModal({ tenantId, onClose, onSubmit }: {
  tenantId: string;
  onClose: () => void;
  onSubmit: (d: { name: string; email: string; password: string; role: string }) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: genPassword(),
    role: 'member',
  });
  const [busy, setBusy] = useState(false);
  const [showPwd, setShowPwd] = useState(true);
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(form); } finally { setBusy(false); }
  };

  return (
    <div className="modal-mask open" onClick={onClose}>
      <div className="dialog tenants-modal" onClick={e => e.stopPropagation()}>
        <header className="dialog-head">
          <h2>添加用户</h2>
          <button className="btn btn-icon" onClick={onClose} aria-label="关闭">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" width="18" height="18"><path d="m5 5 10 10M5 15 15 5"/></svg>
          </button>
        </header>
        <form onSubmit={submit}>
          <div className="dialog-body">
            <div className="tm-field"><label>姓名</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="用户显示名称" /></div>
            <div className="tm-field"><label>邮箱 <em>必填</em></label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} required /></div>
            <div className="tm-field">
              <label>初始密码</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  required
                  minLength={6}
                  style={{ flex: 1 }}
                />
                <button type="button" className="btn btn-secondary" onClick={() => set('password', genPassword())} style={{ padding: '0 10px', fontSize: 12, whiteSpace: 'nowrap' }}>
                  随机生成
                </button>
              </div>
              <small>用户首次登录后可自行修改；密码将在用户列表中显示，直到用户修改为止</small>
            </div>
            <div className="tm-field">
              <label>角色</label>
              <select value={form.role} onChange={e => set('role', e.target.value)}>
                <option value="admin">管理员</option>
                <option value="member">普通用户</option>
              </select>
            </div>
          </div>
          <footer className="dialog-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>取消</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? '创建中…' : '创建用户'}</button>
          </footer>
        </form>
      </div>
    </div>
  );
}

export default TenantsView;
